import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Session } from '../../types/session';
import { AppDispatch } from '../index';
import {
    createSession,
    getStoredSessionId,
    clearStoredSessionId,
    getStoredCookieHash,
    getHistoryTopics,
    getHistoryTopicSummary,
    HistoryTopic,
    getStoredDataParam,
    getUrlParamsAsObject,
    parseDataParam,
    getStoredCountry
} from '@@service/api';
import { STREAM_URL } from '@@service/connection';
import { greeting, errorMessage as fallbackErrorMessage } from '@/common/greeting';

// 定义流式事件类型
interface StreamEvent {
  type: 'user' | 'assistant' | 'content' | 'tool_call' | 'tool_result' | 'button' | 'done' | 'error' | 'progress' | 'stats' | 'survey' | 'close' | 'form';
  content?: string;
  data?: any;
  id?: string;
  tool_call?: any;
  tool_call_id?: string;
  name?: string;
  buttons?: FooterButton[];
  agent?: string;
  form?: FormPayload;
}

// 后端推送的动态表单信号
export interface FormPayload {
  form_id: string;
  form_type: string;
  title?: string;
  description?: string;
  prefill?: Record<string, string>;
  readonly_fields?: string[];
  required_fields?: string[];
  agreement_required?: boolean;
  agreement_text?: string;
  cancel_confirm_text?: string;
}

// 提交给后端的表单回包
export interface FormSubmitPayload {
  form_id: string;
  form_type: string;
  status: 'submitted' | 'cancelled';
  values?: Record<string, any>;
}

// 底部按钮类型定义
interface FooterButtonEvent {
  type: 'quick_reply' | 'url' | 'timeout_action';
  reply_text?: string;
  url?: string;
  action?: 'resolved' | 'more_help';
}

export interface FooterButton {
  label: string;
  event: FooterButtonEvent;
}

// 头部渲染用的工具活动类型定义
interface ToolCallItem {
  id: string;
  think_title?: string;
  thinking?: string;
}

export interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: number;
  type?: 'text' | 'image' | 'pdf' | 'html' | 'survey' | 'history-topics';
  images?: string[];
  // 重命名为更通用的attachments，支持所有文件类型
  attachments?: string[];
  // 用于存储历史topic列表
  topics?: HistoryTopic[];
}

// 流式渲染段：工具调用或内容文本，按顺序排列
export interface StreamingSegment {
  type: 'tool' | 'content';
  // type=content 时使用
  content?: string;
  // type=tool 时使用，存放该段的工具调用信息
  toolActivities?: {
    byId: Record<string, { id: string; thinkTitle?: string; thinking?: string }>;
    order: string[];
    completed?: boolean;
  };
}

// 历史消息段快照
export type SegmentsSnapshot = StreamingSegment[];

export interface ChatState {
  messages: Message[];
  isLoading: boolean;
  error: string | null;
  currentSessionId: string | null;
  sessionHistory: Session[];
  isSessionLoading: boolean;
  // 添加流式传输相关状态
  status: 'idle' | 'submitted' | 'streaming' | 'error';
  streamingAssistantMessage: string;
  streamingBubbleLoading: boolean;
  isAiStreaming: boolean;
  currentAgent?: string;
  // 气泡头部展示用的工具活动（本轮的思考与结果）
  toolActivities: {
    byId: Record<string, { id: string; thinkTitle?: string; thinking?: string }>;
    order: string[];
    // 标记是否已完成
    completed?: boolean;
  };
  // 流式渲染段：按顺序排列的工具/内容块
  streamingSegments: StreamingSegment[];
  // 流式错误信息（用于在气泡 header 展示）
  streamError: {
    content: string;
    data?: any;
    timestamp?: number;
  } | null;
  // 历史助手消息对应的工具活动快照（用于在历史气泡头部展示折叠面板）
  toolActivitiesHistory: Record<string, {
    byId: Record<string, { id: string; thinkTitle?: string; thinking?: string }>;
    order: string[];
    completed?: boolean;
    streamError?: ChatState['streamError'];
  }>;
  // 历史消息段快照
  segmentsHistory: Record<string, SegmentsSnapshot>;
  // 当前流式气泡的底部按钮（来自 SSE button 事件）
  streamingFooterButtons?: FooterButton[];
  // 待处理的底部按钮
  pendingFooterButtons?: FooterButton[];
  // 历史消息的底部按钮快照
  footerButtonsHistory: Record<string, FooterButton[] | undefined>;
  // 当前流式消息的类型（用于在 footer 渲染 survey 等，不影响正文）
  currentMessageType?: Message['type'];
  // 历史消息的调查组件标记
  footerSurveyHistory: Record<string, boolean | undefined>;
  // 会话开放状态
  isOpen: boolean;
  // 问卷可填写状态（独立于会话是否开放）
  isSurveyEnabled: boolean;
  // 后端推送的当前待填表单（为空表示不展示）
  activeForm: FormPayload | null;
}

const initialState: ChatState = {
    messages: [],
    isLoading: false,
    error: null,
    currentSessionId: getStoredSessionId(),
    sessionHistory: [],
    isSessionLoading: false,
    // 初始化流式传输相关状态
    status: 'idle',
    streamingAssistantMessage: '',
    streamingBubbleLoading: false,
    isAiStreaming: false,
    currentAgent: undefined,
    toolActivities: {
        byId: {},
        order: []
    },
    streamingSegments: [],
    streamError: null,
    toolActivitiesHistory: {},
    segmentsHistory: {},
    streamingFooterButtons: undefined,
    pendingFooterButtons: undefined,
    footerButtonsHistory: {},
    currentMessageType: undefined,
    footerSurveyHistory: {},
    isOpen: true,
    isSurveyEnabled: false,
    activeForm: null
};

const chatUrl = STREAM_URL;

// 将后端返回的历史记录（history-data.json 格式）转换为本地状态结构
function transformHistoryItems(history: any[] = []) {
    const messages: Message[] = [];
    const toolActivitiesHistory: ChatState['toolActivitiesHistory'] = {};
    const segmentsHistory: ChatState['segmentsHistory'] = {};
    const footerButtonsHistory: ChatState['footerButtonsHistory'] = {};
    const footerSurveyHistory: ChatState['footerSurveyHistory'] = {};

    history.forEach((item: any, index: number) => {
        const role = (item?.role === 'assistant' || item?.role === 'user') ? item.role : 'assistant';
        const ts = typeof item?.timestamp === 'string' ? Date.parse(item.timestamp) : (
            typeof item?.timestamp === 'number' ? item.timestamp : Date.now()
        );
        const messageId = item?.id || `restored-${role}-${index}-${isNaN(ts) ? Date.now() : ts}`;

        messages.push({
            id: messageId,
            content: String(item?.content ?? ''),
            role,
            timestamp: isNaN(ts) ? Date.now() : ts,
            // 若历史项包含 survey=true，则将该消息类型标记为 survey
            type: item?.survey === true ? 'survey' : undefined,
            // 新增处理: 合并图片和文件到 attachments
            images: item?.images ? [ ...item.images ] : undefined,
            attachments: [
                ...(Array.isArray(item?.images) ? item.images : []),
                ...(Array.isArray(item?.files) ? item.files : [])
            ].length > 0 ? [
                ...(Array.isArray(item?.images) ? item.images : []),
                ...(Array.isArray(item?.files) ? item.files : [])
            ] : undefined
        });

        if (role === 'assistant') {
            // 工具调用快照
            const toolCalls: any[] = Array.isArray(item?.tool_calls) ? item.tool_calls : [];
            if (toolCalls.length > 0) {
                const byId: Record<string, { id: string; thinkTitle?: string; thinking?: string }> = {};
                const order: string[] = [];
                toolCalls.forEach((tc: any, idx: number) => {
                    const tcId = tc?.id || `tc-${index}-${idx}`;
                    byId[tcId] = {
                        id: tcId,
                        thinkTitle: tc?.think_title,
                        thinking: tc?.thinking
                    };
                    order.push(tcId);
                });
                toolActivitiesHistory[messageId] = {
                    byId,
                    order,
                    completed: true
                } as any;
                // 同步生成 segments 快照（工具段 + 内容段）
                const segs: StreamingSegment[] = [
                    { type: 'tool', toolActivities: { byId, order, completed: true } }
                ];
                const msgContent = String(item?.content ?? '');
                if (msgContent) {
                    segs.push({ type: 'content', content: msgContent });
                }
                segmentsHistory[messageId] = segs;
            }

            // 底部按钮快照
            const buttons = Array.isArray(item?.buttons) ? item.buttons : undefined;
            if (buttons && buttons.length > 0) {
                footerButtonsHistory[messageId] = buttons as any;
            }
            // 调查标记（与按钮互斥，由后端保证）
            if (item?.survey === true) {
                footerSurveyHistory[messageId] = true;
            }
        }
    });

    return { messages, toolActivitiesHistory, segmentsHistory, footerButtonsHistory, footerSurveyHistory };
}

// 初始化会话
export const initializeSession = createAsyncThunk(
    'chat/initializeSession',
    async (params?: { urlParams?: Record<string, string>; country?: string }) => {
        // 优先用传入的 urlParams，其次从 localStorage 回读
        const rawParams = params?.urlParams || getUrlParamsAsObject();
        // countryCode 用用户真实选择的国家；sourceCountryCode 保留 URL 原始值；UK 统一转为 GB
        const sourceCountry = rawParams?.countryCode === 'UK' ? 'GB' : (rawParams?.countryCode || '');
        const selectedCountry = params?.country || rawParams?.countryCode;
        const normalizedCountryCode = selectedCountry === 'UK' ? 'GB' : selectedCountry;
        let clientInfo: Record<string, string> | undefined = rawParams
            ? { ...rawParams, countryCode: normalizedCountryCode || '', sourceCountryCode: sourceCountry }
            : { countryCode: params?.country || getStoredCountry() || '' };
        // 解析 data 参数并合并（保留 data 原始值）
        if (clientInfo?.data) {
            const parsed = parseDataParam(clientInfo.data);
            if (parsed) {
                clientInfo = { ...clientInfo, ...parsed };
            }
        }
        const country = params?.country;
        const newSession = await createSession({ clientInfo, country });

        if (!newSession.is_restored) {
            return {
                sessionId: newSession.session_id,
                isRestored: false,
                greeting: greeting,
                messages: [],
                toolActivitiesHistory: {},
                footerButtonsHistory: {},
                is_open: typeof newSession.is_open === 'boolean' ? newSession.is_open : true
            };
        }

        // is_restored = true 但 chat_history 为空时，回退展示 greeting
        if (!newSession.chat_history || newSession.chat_history.length === 0) {
            return {
                sessionId: newSession.session_id,
                isRestored: true,
                greeting: greeting,
                messages: [],
                toolActivitiesHistory: {},
                footerButtonsHistory: {},
                is_open: typeof newSession.is_open === 'boolean' ? newSession.is_open : true
            };
        }

        const {
            messages,
            toolActivitiesHistory,
            segmentsHistory,
            footerButtonsHistory,
            footerSurveyHistory
        } = transformHistoryItems(newSession.chat_history || []);
        return {
            sessionId: newSession.session_id,
            isRestored: true,
            greeting: greeting,
            messages,
            toolActivitiesHistory,
            segmentsHistory,
            footerButtonsHistory,
            footerSurveyHistory,
            is_open: typeof newSession.is_open === 'boolean' ? newSession.is_open : true
        };
    }
);

// 创建新会话
export const createNewSession = createAsyncThunk(
    'chat/createNewSession',
    async (params?: { country?: string }) => {
        // 强制创建新会话，使用所有 URL 参数作为 client_info
        const rawParams = getUrlParamsAsObject();
        // countryCode 用用户真实选择的国家；sourceCountryCode 保留 URL 原始值；UK 统一转为 GB
        const sourceCountry = rawParams?.countryCode === 'UK' ? 'GB' : (rawParams?.countryCode || '');
        const selectedCountry = params?.country || rawParams?.countryCode;
        const normalizedCountryCode = selectedCountry === 'UK' ? 'GB' : selectedCountry;
        let clientInfo: Record<string, string> = rawParams
            ? { ...rawParams, countryCode: normalizedCountryCode || '', sourceCountryCode: sourceCountry }
            : { countryCode: params?.country || getStoredCountry() || '' };
        // 解析 data 参数并合并（保留 data 原始值）
        if (clientInfo?.data) {
            const parsed = parseDataParam(clientInfo.data);
            if (parsed) {
                clientInfo = { ...clientInfo, ...parsed };
            }
        }
        const newSession = await createSession({ force_new: true, clientInfo, country: params?.country });
        return {
            sessionId: newSession.session_id,
            messages: [],
            is_open: typeof newSession.is_open === 'boolean' ? newSession.is_open : true
        };
    }
);

// 获取历史topic列表
export const fetchHistoryTopics = createAsyncThunk(
    'chat/fetchHistoryTopics',
    async (_, { dispatch }) => {
        // 添加用户消息
        const userMessage: Message = {
            id: `user-${Date.now()}`,
            content: 'Historical Issue',
            role: 'user',
            timestamp: Date.now()
        };
        dispatch(addMessage(userMessage));

        // 获取topic列表
        const topics = await getHistoryTopics(getStoredDataParam() || '');

        // 添加bot消息，包含topic列表
        const botMessage: Message = {
            id: `assistant-${Date.now()}`,
            content: topics && topics.length === 0 ? 'No history' : '',
            role: 'assistant',
            timestamp: Date.now(),
            type: 'history-topics',
            topics
        };
        dispatch(addMessage(botMessage));

        return { topics };
    }
);

// 获取历史topic的summary
export const fetchTopicSummary = createAsyncThunk(
    'chat/fetchTopicSummary',
    async (payload: { topicId: string; topicText: string }, { dispatch }) => {
        const { topicId, topicText } = payload;

        // 先添加用户消息（显示topic文本）
        const userMessage: Message = {
            id: `user-${Date.now()}`,
            content: topicText,
            role: 'user',
            timestamp: Date.now()
        };
        dispatch(addMessage(userMessage));

        // 获取summary
        const summary = await getHistoryTopicSummary(topicId, topicText);

        // 添加bot消息，展示summary
        const botMessage: Message = {
            id: `assistant-${Date.now()}`,
            content: summary,
            role: 'assistant',
            timestamp: Date.now()
        };
        dispatch(addMessage(botMessage));

        return { topicId, topicText, summary };
    }
);

// 发送消息（支持流式返回）
type SendMessagePayload = {
    content?: string;
    attachments?: string[];
    billable?: { status: boolean; data?: any };
    formSubmit?: FormSubmitPayload;
};

export const sendMessage = createAsyncThunk(
    'chat/sendMessage',
    async (payload: SendMessagePayload, { getState, dispatch }) => {
        const state = getState() as any;
        const chatState = state.chat as ChatState;
        const sessionId = chatState.currentSessionId;

        // 检查会话ID是否存在
        if (!sessionId) {
            throw new Error('没有活跃的会话');
        }

        const isBillableMode = !!payload.billable;
        const isFormSubmitMode = !!payload.formSubmit;
        const text = (payload.content || '').trim();
        const hasAttachments = Array.isArray(payload.attachments) && payload.attachments.length > 0;
        // 检查是否正在处理中；放开仅附件发送（text 可为空但有附件）；放开 billable-only / form-submit-only
        if (chatState.status === 'streaming' || chatState.status === 'submitted') {
            return;
        }
        // 文本与附件至少一个存在；若为 billable-only / form-submit-only 则不需要文本/附件
        if (!isBillableMode && !isFormSubmitMode && !text && !hasAttachments) {
            return;
        }

        // 设置状态为已提交
        dispatch(setStreamingStatus('submitted'));

        // 重置会话上下文状态（每次新对话重置工具状态、进度和统计信息）
        dispatch(resetSessionContext());

        // 重置流式消息
        dispatch(resetStreamingAssistantMessage());
        // 清理上一轮的流式错误
        dispatch(setStreamError(null));

        // 添加用户消息到消息列表（billable-only / form-submit-only 不产生用户气泡）
        let userMessage: Message | null = null;
        if (!isBillableMode && !isFormSubmitMode) {
            userMessage = {
                id: `user-${Date.now()}`,
                content: text,
                role: 'user',
                timestamp: Date.now(),
                attachments: payload.attachments
            };
            dispatch(addMessage(userMessage));
            // 清理上一轮所有历史 bot 消息的底部按钮（满足“新发消息后移除旧按钮”的需求）
            dispatch(clearAllFooterButtonsHistory());
        }

        // 指令触发的本地模拟（用于可视化测试，无需后端）
        if (text.startsWith('/mock')) {
            // 进入流式阶段，并开启 loading
            dispatch(setStreamingStatus('streaming'));
            dispatch(setStreamingBubbleLoading(true));
            await simulateMockStream(dispatch as AppDispatch, sessionId, text);
            return { userMessage };
        }

        try {
            // 启动请求前先中断前一个
            if (streamAbortController) {
                streamAbortController.abort();
            }
            streamAbortController = new AbortController();
            // 设置状态为流式传输中，并开启 loading 以避免空白
            dispatch(setStreamingStatus('streaming'));
            dispatch(setStreamingBubbleLoading(true));
            // 开始流式传输
            const requestBody: any = {
                message: (isBillableMode || isFormSubmitMode) ? '' : text,
                user_id: 'web_user',
                session_id: sessionId,
                files: hasAttachments ? payload.attachments : undefined
            };
            if (isBillableMode) {
                requestBody.billable = payload.billable;
            }
            if (isFormSubmitMode) {
                requestBody.form_submit = payload.formSubmit;
            }

            const res = await fetch(chatUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Cookie-Hash': getStoredCookieHash()
                },
                // 允许跨域请求携带 cookie
                // credentials: 'include',
                body: JSON.stringify(requestBody),
                // 新增signal用于abort流式fetch
                signal: streamAbortController.signal
            });

            if (!res.ok) {
                throw new Error(`Request failed with status ${res.status}`);
            }

            const contentType = res.headers.get('content-type') || '';
            if (contentType.includes('application/json')) {
                const result: StreamEvent = await res.json();
                processStreamEvent(result, sessionId, dispatch as AppDispatch);
            } else {
                await processSseResponse(res, sessionId, dispatch as AppDispatch);
            }

            dispatch(setStreamingStatus('idle'));

            return { userMessage };
        } catch (err) {
            // AbortError静默处理，不弹error
            if (err && typeof err === 'object' && (err as any).name === 'AbortError') {
                dispatch(setStreamingStatus('idle'));
                return;
            }
            // 网络请求失败时，使用兜底话术生成助手回复
            dispatch(finalizeAndSnapshotOnError({
                fallbackContent: fallbackErrorMessage,
                forceFallback: true,
                skipSnapshots: true
            }));
            dispatch(setStreamingStatus('idle'));
            dispatch(setStreamingBubbleLoading(false));
            dispatch(setIsAiStreaming(false));
            // 清理流式按钮
            dispatch(setStreamingFooterButtons(undefined));
            const requestErrorMessage = (err as Error).message || 'Failed to send message. Please try again.';
            console.error('发送消息失败:', requestErrorMessage);
            // 不再throw，避免触发全局错误处理
            return { userMessage };
        }
    }
);

// 处理单行 SSE 的辅助函数
async function handleSseLine(line: string, sessionId: string, dispatch: AppDispatch) {
    const trimmed = line.trim();
    if (!trimmed || !trimmed.startsWith('data: ')) return;

    try {
        const jsonStr = trimmed.slice(6);
        const obj: StreamEvent = JSON.parse(jsonStr);
        processStreamEvent(obj, sessionId, dispatch);
    } catch (e) {
        console.warn('解析流式消息失败:', e, '原始数据:', trimmed);
    }
}

async function processSseResponse(res: Response, sessionId: string, dispatch: AppDispatch) {
    if (!res.body) {
        throw new Error('No response body');
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
        const { done, value } = await reader.read();
        if (done) {
            console.log('流式读取完成');
            break;
        }

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
            await handleSseLine(line, sessionId, dispatch);
        }
    }

    if (buffer.trim()) {
        const trimmed = buffer.trim();
        if (trimmed.startsWith('data: ')) {
            try {
                const obj: StreamEvent = JSON.parse(trimmed.slice(6));
                processStreamEvent(obj, sessionId, dispatch);
            } catch (e) {
                console.warn('解析最后流式消息失败:', e);
            }
        }
    }
}

// 模块级变量，用于在多次processStreamEvent调用间暂存按钮
let modulePendingButtons: FooterButton[] | null = null;

// 处理流式事件的主要函数
function processStreamEvent(obj: StreamEvent, sessionId: string, dispatch: AppDispatch) {
    switch (obj.type) {
        case 'tool_call':
            handleToolCall(obj, dispatch);
            break;
        case 'tool_result':
            handleToolResult(obj, dispatch);
            break;
        case 'button':
            // 暂时不处理button事件，等done事件时再处理
            if (obj.buttons && Array.isArray(obj.buttons) && obj.buttons.length > 0) {
                // 使用模块级变量暂存按钮
                modulePendingButtons = obj.buttons;
            }
            break;
        case 'content':
            handleAiMessage(obj, dispatch);
            break;
        case 'survey':
            handleSurvey(dispatch);
            break;
        case 'close':
            handleCloseEvent(obj, dispatch);
            break;
        case 'form':
            if (obj.form && obj.form.form_id) {
                dispatch(setActiveForm(obj.form));
            }
            break;
        case 'done':
            // 处理存储的按钮
            if (modulePendingButtons && modulePendingButtons.length > 0) {
                dispatch(setStreamingFooterButtons(modulePendingButtons));
                // 清除临时存储
                modulePendingButtons = null;
            }
            handleCompleted(dispatch);
            break;
        case 'error':
            // 清除临时存储
            modulePendingButtons = null;
            handleError(obj, dispatch);
            break;
        default:
            console.log('未处理的事件类型:', obj.type);
    }
}

// 本地模拟流式事件
async function simulateMockStream(dispatch: AppDispatch, sessionId: string, command: string) {
    const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

    const mkToolCall = (id: string, title: string, thinking: string): any => ({
        type: 'tool_call',
        tool_call: { id, think_title: title, thinking },
        timestamp: Date.now()
    });

    const mkToolResult = (id: string): any => ({
        type: 'tool_result',
        tool_call_id: id,
        content: 'success',
        timestamp: Date.now()
    });

    const mkContent = (text: string): any => ({
        type: 'content',
        content: text,
        timestamp: Date.now()
    });

    // /mock rich — 测试丰富 markdown 渲染（代码块、表格、引用等）
    if (command.startsWith('/mock rich')) {
        processStreamEvent(mkToolCall('tc1', 'Analyzing code', 'Reviewing the source files...'), sessionId, dispatch);
        await sleep(500);
        processStreamEvent(mkToolResult('tc1'), sessionId, dispatch);
        await sleep(300);

        const richContent = `Here's what I found in the codebase:

## Issue Summary

The bug is in the \`handleSubmit\` function. Here's the fix:

\`\`\`typescript
async function handleSubmit(data: FormData) {
  const result = await api.post('/submit', data);
  if (result.status === 200) {
    notify('Success!');
  }
}
\`\`\`

### Key Changes

| File | Change | Status |
|------|--------|--------|
| \`api.ts\` | Fixed timeout | Done |
| \`utils.ts\` | Added retry | Done |
| \`index.tsx\` | Updated UI | Pending |

> **Note:** Make sure to run \`npm test\` before deploying.

Here are the next steps:

1. **Review** the changes above
2. **Test** locally with \`npm run dev\`
3. **Deploy** to staging first

Let me know if you need anything else!`;

        for (const chunk of richContent.match(/[\s\S]{1,30}/g) || []) {
            processStreamEvent(mkContent(chunk), sessionId, dispatch);
            await sleep(50);
        }
        await sleep(200);
        processStreamEvent({ type: 'done' } as any, sessionId, dispatch);
        return;
    }

    // /mock interleave — 测试工具与内容交替输出
    if (command.startsWith('/mock interleave')) {
        // 第一组工具调用
        processStreamEvent(mkToolCall('tc1', 'Searching user info', 'Looking up account details...'), sessionId, dispatch);
        await sleep(500);
        processStreamEvent(mkToolCall('tc2', 'Checking warranty', 'Verifying warranty status...'), sessionId, dispatch);
        await sleep(500);
        processStreamEvent(mkToolResult('tc1'), sessionId, dispatch);
        await sleep(300);

        // 第一段内容
        processStreamEvent(mkContent('I found your account information. Let me check a few more things.\n\n'), sessionId, dispatch);
        await sleep(400);

        // 第二组工具调用
        processStreamEvent(mkToolCall('tc3', 'Fetching order history', 'Retrieving recent orders...'), sessionId, dispatch);
        await sleep(600);
        processStreamEvent(mkToolResult('tc3'), sessionId, dispatch);
        await sleep(300);

        // 第二段内容
        for (const chunk of [ 'Based on ', 'my analysis, ', 'your device ', 'is still under ', 'warranty. ', '\n\nHere are ', 'your options:\n', '1. **Remote repair** — free\n', '2. **On-site repair** — $50 service fee\n', '3. **Mail-in repair** — free shipping\n' ]) {
            processStreamEvent(mkContent(chunk), sessionId, dispatch);
            await sleep(100);
        }
        await sleep(200);
        processStreamEvent({ type: 'done' } as any, sessionId, dispatch);
        return;
    }

    // /mock tools — 简单工具 + 内容
    processStreamEvent(mkToolCall('tc1', 'Looking up information', 'Searching the database...'), sessionId, dispatch);
    await sleep(800);
    processStreamEvent(mkToolResult('tc1'), sessionId, dispatch);
    await sleep(300);

    for (const chunk of [ 'Hello! ', 'I found ', 'the information ', 'you requested. ', 'Everything looks good.' ]) {
        processStreamEvent(mkContent(chunk), sessionId, dispatch);
        await sleep(150);
    }
    await sleep(200);
    processStreamEvent({ type: 'done' } as any, sessionId, dispatch);
}

// 处理工具调用事件
function handleToolCall(obj: StreamEvent, dispatch: AppDispatch) {
    // 进入工具阶段，清空AI流式标记
    dispatch(setIsAiStreaming(false));
    // 设置 loading 以便在消息气泡中显示进度指示（不展示文本）
    dispatch(setStreamingBubbleLoading(true));

    // 新增/更新工具调用项
    const toolCall = obj.tool_call;
    if (toolCall && toolCall.id) {
        dispatch(upsertToolCalls([ toolCall ]));
        // 同步更新到 streamingSegments
        dispatch(appendToolSegment(toolCall));
    }
}

// 处理工具结果事件
function handleToolResult(obj: StreamEvent, dispatch: AppDispatch) {
    // 仍处于工具阶段，结果展示在头部区域
    dispatch(setIsAiStreaming(false));
    // 不再处理单独的 tool_result，等待 done 事件统一显示完成状态
}

// 处理错误事件
function handleError(obj: StreamEvent, dispatch: AppDispatch) {
    // 清理气泡 header 错误展示
    dispatch(setStreamError(null));
    // 使用兜底话术生成助手回复
    dispatch(finalizeAndSnapshotOnError({
        fallbackContent: fallbackErrorMessage,
        forceFallback: true,
        skipSnapshots: true
    }));
    // 结束当前轮状态，避免出现“空气泡”并清理进行中标记
    dispatch(setStreamingBubbleLoading(false));
    dispatch(setIsAiStreaming(false));
    dispatch(setStreamingStatus('idle'));
    // 清理流式按钮
    dispatch(setStreamingFooterButtons(undefined));
}

// 处理AI消息事件（流式拼接内容）
function handleAiMessage(obj: StreamEvent, dispatch: AppDispatch) {
    if (obj.content) {
        // 关闭loading状态
        dispatch(setStreamingBubbleLoading(false));
        // 确保进入AI阶段（只在第一次时重置内容）
        dispatch(setIsAiStreaming(true));
        // 累加内容
        dispatch(updateStreamingAssistantMessage(obj.content));
        // 同步更新到 streamingSegments
        dispatch(appendContentSegment(obj.content));
    }
}

// 处理调查表单事件：直接插入一条 assistant 消息，类型为 survey
function handleSurvey(dispatch: AppDispatch) {
    // 不再打断流式，也不新增独立消息，仅标记当前流式消息类型为 survey
    dispatch(setCurrentMessageType('survey'));
    // 开启问卷可填写
    dispatch(setSurveyEnabled(true));
}

// 处理会话关闭事件
function handleCloseEvent(obj: StreamEvent, dispatch: AppDispatch) {
    const content = obj.content || 'We’re sorry for the inconvenience. For more help, please visit [Contact US](https://pcsupport.lenovo.com/us/en/contact-us?linktrack=footer%3Asupport_contact%20us) to see the support options available to you.';
    dispatch(setIsOpen(false));
    dispatch(setCurrentAgent(obj.agent));
    dispatch(addMessage({
        id: `assistant-close-${Date.now()}`,
        content,
        role: 'assistant',
        timestamp: Date.now()
    }));
    dispatch(setStreamingStatus('idle'));
    dispatch(setStreamingBubbleLoading(false));
    dispatch(setIsAiStreaming(false));
}

// 处理完成事件
function handleCompleted(dispatch: AppDispatch) {
    // 标记工具调用已完成
    dispatch(setToolActivitiesCompleted(true));
    // 将当前工具活动与错误快照到即将生成的助手消息上
    const snapshotId = `assistant-${Date.now()}`;
    dispatch(snapshotToolActivitiesToMessage({ messageId: snapshotId }));
    // 快照 segments
    dispatch(snapshotSegmentsToMessage({ messageId: snapshotId }));
    // 快照按钮到消息
    dispatch(snapshotFooterButtonsToMessage({ messageId: snapshotId }));
    dispatch(finalizeStreamingAssistantMessageWithId({
        id: snapshotId,
        fallbackContent: fallbackErrorMessage
    }));
    dispatch(setStreamingStatus('idle'));
    dispatch(setStreamingBubbleLoading(false));
    dispatch(setIsAiStreaming(false));
    // 保留历史在快照中，这里清空当前活动
    dispatch(resetToolActivities());
    // 清理 streamingSegments
    dispatch(resetStreamingSegments());
    // 清理本轮错误信息
    dispatch(setStreamError(null));
    // 清理流式按钮
    dispatch(setStreamingFooterButtons(undefined));
}

// ====== 全局 AbortController: 用于流式 fetch 主动中断 ======
let streamAbortController: AbortController | null = null;
function abortCurrentStream() {
    if (streamAbortController) {
        streamAbortController.abort();
        streamAbortController = null;
    }
}


const chatSlice = createSlice({
    name: 'chat',
    initialState,
    reducers: {
        clearMessages: (state) => {
            state.messages = [];
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        // 设置/清理流式错误
        setStreamError: (state, action: PayloadAction<ChatState['streamError']>) => {
            state.streamError = action.payload || null;
        },
        setCurrentSessionId: (state, action: PayloadAction<string | null>) => {
            state.currentSessionId = action.payload;
        },
        setCurrentAgent: (state, action: PayloadAction<string | undefined>) => {
            state.currentAgent = action.payload;
        },
        clearSession: (state) => {
            state.currentSessionId = null;
            state.messages = [];
            state.streamingAssistantMessage = '';
            state.streamingBubbleLoading = false;
            state.status = 'idle';
            state.streamError = null;
            state.isSurveyEnabled = false;
            clearStoredSessionId();
        },
        // 添加流式传输相关的reducers
        setStreamingStatus: (state, action: PayloadAction<'idle' | 'submitted' | 'streaming' | 'error'>) => {
            state.status = action.payload;
        },
        updateStreamingAssistantMessage: (state, action: PayloadAction<string>) => {
            if (state.isAiStreaming) {
                // AI阶段：拼接
                state.streamingAssistantMessage += action.payload;
            } else {
                // 非AI阶段：覆盖（工具状态/工具结果）
                state.streamingAssistantMessage = action.payload;
            }
        },
        setStreamingAssistantMessage: (state, action: PayloadAction<string>) => {
            state.streamingAssistantMessage = action.payload;
        },
        // 设置当前流式消息的类型（用于footer渲染survey等，不影响正文）
        setCurrentMessageType: (state, action: PayloadAction<Message['type'] | undefined>) => {
            state.currentMessageType = action.payload;
        },
        resetStreamingAssistantMessage: (state) => {
            state.streamingAssistantMessage = '';
        },
        setStreamingBubbleLoading: (state, action: PayloadAction<boolean>) => {
            state.streamingBubbleLoading = action.payload;
        },
        setIsAiStreaming: (state, action: PayloadAction<boolean>) => {
            // 不在类型切换时清空已累加的内容，避免被 tool/button 打断后丢失
            state.isAiStreaming = action.payload;
        },
        addMessage: (state, action: PayloadAction<Message>) => {
            state.messages.push(action.payload);
        },
        // 控制问卷是否允许填写
        setSurveyEnabled: (state, action: PayloadAction<boolean>) => {
            state.isSurveyEnabled = action.payload;
        },
        finalizeStreamingAssistantMessage: (state) => {
            const content = state.streamingAssistantMessage;
            if (content && content.trim().length > 0) {
                state.messages.push({
                    id: `assistant-${Date.now()}`,
                    content,
                    role: 'assistant',
                    timestamp: Date.now(),
                    type: state.currentMessageType
                });
                state.streamingAssistantMessage = '';
                state.currentMessageType = undefined;
            }
        },
        // 带指定 id 的最终化（用于在完成前先记录快照 id）
        finalizeStreamingAssistantMessageWithId: (
            state,
            action: PayloadAction<{ id: string; fallbackContent?: string }>
        ) => {
            let content = state.streamingAssistantMessage;
            if ((!content || content.trim().length === 0) && action.payload?.fallbackContent) {
                content = action.payload.fallbackContent;
            }
            if (content && content.trim().length > 0) {
                state.messages.push({
                    id: action.payload.id,
                    content,
                    role: 'assistant',
                    timestamp: Date.now(),
                    type: state.currentMessageType
                });
                state.streamingAssistantMessage = '';
                state.currentMessageType = undefined;
            }
        },
        // 在完成前将当前工具活动与错误信息快照到指定消息 id
        snapshotToolActivitiesToMessage: (state, action: PayloadAction<{ messageId: string }>) => {
            const { messageId } = action.payload;
            state.toolActivitiesHistory[messageId] = {
                byId: { ...state.toolActivities.byId },
                order: [ ...state.toolActivities.order ],
                completed: state.toolActivities.completed,
                streamError: state.streamError ? { ...state.streamError } : undefined
            };
        },
        resetSessionContext: (state) => {
            // 重置会话上下文时清理流式消息状态
            state.streamingAssistantMessage = '';
            state.streamingBubbleLoading = false;
            // 清理工具可视化状态
            state.toolActivities = { byId: {}, order: [] };
            state.streamingSegments = [];
            // 清理上一轮错误
            state.streamError = null;
            // 清理按钮
            state.streamingFooterButtons = undefined;
            state.pendingFooterButtons = undefined;
            // 清理当前流式消息类型
            state.currentMessageType = undefined;
            // 默认重置问卷可填写
            state.isSurveyEnabled = false;
        },
        // 出错时：若有正文保留原正文，否则用 fallbackContent 作为正文；并快照工具步骤与错误
        finalizeAndSnapshotOnError: (
            state,
            action: PayloadAction<{ fallbackContent: string; forceFallback?: boolean; skipSnapshots?: boolean }>
        ) => {
            const existing = action.payload?.forceFallback ? '' : state.streamingAssistantMessage;
            const hasContent = !!existing && existing.trim().length > 0;
            const shouldUseFallback = action.payload?.forceFallback || !hasContent;
            const contentToUse = shouldUseFallback ? (action.payload?.fallbackContent ?? '') : existing;
            const id = `assistant-${Date.now()}`;
            // 将正文落为历史消息
            state.messages.push({
                id,
                content: contentToUse,
                role: 'assistant',
                timestamp: Date.now()
            });
            state.streamingAssistantMessage = '';
            const skipSnapshots = !!action.payload?.skipSnapshots;
            if (!skipSnapshots) {
                // 快照工具步骤与错误
                state.toolActivitiesHistory[id] = {
                    byId: { ...state.toolActivities.byId },
                    order: [ ...state.toolActivities.order ],
                    completed: state.toolActivities.completed,
                    streamError: state.streamError ? { ...state.streamError } : undefined
                };
                // 快照 segments
                if (state.streamingSegments.length > 0) {
                    state.segmentsHistory[id] = state.streamingSegments.map(seg => {
                        if (seg.type === 'tool' && seg.toolActivities) {
                            return { ...seg, toolActivities: { ...seg.toolActivities, completed: true } };
                        }
                        return seg;
                    });
                }
                // 快照按钮
                if (state.streamingFooterButtons && state.streamingFooterButtons.length > 0) {
                    state.footerButtonsHistory[id] = [ ...state.streamingFooterButtons ];
                } else {
                    state.footerButtonsHistory[id] = undefined;
                }
            }
            // 清理当前活动与错误
            state.toolActivities = { byId: {}, order: [] };
            state.streamingSegments = [];
            state.streamError = null;
            // 清理按钮
            state.streamingFooterButtons = undefined;
        },
        // 从 SSE 的 'tool_call' 事件新增/更新工具调用项
        upsertToolCalls: (state, action: PayloadAction<ToolCallItem[]>) => {
            for (const item of action.payload) {
                const id = item.id;
                if (!id) continue;
                const existed = state.toolActivities.byId[id];
                if (!existed) {
                    state.toolActivities.byId[id] = {
                        id,
                        thinkTitle: item.think_title,
                        thinking: item.thinking
                    };
                    state.toolActivities.order.push(id);
                } else {
                    if (item.think_title !== undefined) existed.thinkTitle = item.think_title;
                    if (item.thinking !== undefined) existed.thinking = item.thinking;
                }
            }
        },
        // 从 SSE 的 'tool_result' 事件追加工具结果（已废弃，不再使用）
        appendToolResults: () => {
            // 不再处理单独的结果，等待 done 事件统一显示完成状态
        },
        // 设置工具活动完成状态
        setToolActivitiesCompleted: (state, action: PayloadAction<boolean>) => {
            state.toolActivities.completed = action.payload;
        },
        // 重置工具活动（单轮结束）
        resetToolActivities: (state) => {
            state.toolActivities = { byId: {}, order: [], completed: false };
        },
        // 追加工具段到 streamingSegments
        appendToolSegment: (state, action: PayloadAction<ToolCallItem>) => {
            const item = action.payload;
            const last = state.streamingSegments[state.streamingSegments.length - 1];
            if (last && last.type === 'tool' && last.toolActivities) {
                // 追加到当前工具段
                const ta = last.toolActivities;
                const existed = ta.byId[item.id];
                if (!existed) {
                    ta.byId[item.id] = {
                        id: item.id,
                        thinkTitle: item.think_title,
                        thinking: item.thinking
                    };
                    ta.order.push(item.id);
                } else {
                    if (item.think_title !== undefined) existed.thinkTitle = item.think_title;
                    if (item.thinking !== undefined) existed.thinking = item.thinking;
                }
            } else {
                // 新建工具段
                state.streamingSegments.push({
                    type: 'tool',
                    toolActivities: {
                        byId: {
                            [item.id]: {
                                id: item.id,
                                thinkTitle: item.think_title,
                                thinking: item.thinking
                            }
                        },
                        order: [ item.id ]
                    }
                });
            }
        },
        // 追加内容段到 streamingSegments
        appendContentSegment: (state, action: PayloadAction<string>) => {
            const last = state.streamingSegments[state.streamingSegments.length - 1];
            if (last && last.type === 'content') {
                last.content = (last.content || '') + action.payload;
            } else {
                state.streamingSegments.push({
                    type: 'content',
                    content: action.payload
                });
            }
        },
        // 快照 segments 到历史
        snapshotSegmentsToMessage: (state, action: PayloadAction<{ messageId: string }>) => {
            const { messageId } = action.payload;
            // 标记所有工具段为 completed
            const segments = state.streamingSegments.map(seg => {
                if (seg.type === 'tool' && seg.toolActivities) {
                    return { ...seg, toolActivities: { ...seg.toolActivities, completed: true } };
                }
                return seg;
            });
            state.segmentsHistory[messageId] = segments;
        },
        // 重置 streamingSegments
        resetStreamingSegments: (state) => {
            state.streamingSegments = [];
        },
        // 设置流式底部按钮
        setStreamingFooterButtons: (state, action: PayloadAction<FooterButton[] | undefined>) => {
            state.streamingFooterButtons = action.payload && action.payload.length > 0 ? action.payload : undefined;
        },

        // 快照按钮到指定消息
        snapshotFooterButtonsToMessage: (state, action: PayloadAction<{ messageId: string }>) => {
            const { messageId } = action.payload;
            if (state.streamingFooterButtons && state.streamingFooterButtons.length > 0) {
                state.footerButtonsHistory[messageId] = [ ...state.streamingFooterButtons ];
            } else {
                state.footerButtonsHistory[messageId] = undefined;
            }
        },
        // 直接为指定消息设置按钮（用于本地生成的系统消息）
        setFooterButtonsHistoryEntry: (
            state,
            action: PayloadAction<{ messageId: string; buttons?: FooterButton[] }>
        ) => {
            const { messageId, buttons } = action.payload;
            if (!messageId) return;
            state.footerButtonsHistory[messageId] = buttons ? buttons.map((btn) => ({
                ...btn,
                event: { ...btn.event }
            })) : undefined;
        },
        // 清空历史消息的底部按钮
        clearAllFooterButtonsHistory: (state) => {
            state.footerButtonsHistory = {};
        },
        setIsOpen: (state, action: PayloadAction<boolean>) => {
            state.isOpen = action.payload;
        },
        // 设置/清除后端推送的动态表单
        setActiveForm: (state, action: PayloadAction<FormPayload | null>) => {
            state.activeForm = action.payload || null;
        },
        // 新增：中断流式任务
        abortStream: (state) => {
            state.status = 'idle';
            state.isAiStreaming = false;
            state.streamingAssistantMessage = '';
            state.streamingBubbleLoading = false;
            state.streamingSegments = [];
            state.streamError = null;
            state.streamingFooterButtons = undefined;
            state.currentMessageType = undefined;
            state.isSurveyEnabled = false;
        }
    },
    extraReducers: (builder) => {
        builder
            // 初始化会话
            .addCase(initializeSession.pending, (state) => {
                state.isSessionLoading = true;
                state.error = null;
            })
            .addCase(initializeSession.fulfilled, (state, action) => {
                state.isSessionLoading = false;
                state.currentSessionId = action.payload.sessionId;
                state.messages = action.payload.messages;
                // 写入历史的工具调用与按钮快照，供 BotItem 按 messageId 渲染
                if (action.payload.toolActivitiesHistory) {
                    state.toolActivitiesHistory = action.payload.toolActivitiesHistory as any;
                }
                if ((action.payload as any).segmentsHistory) {
                    state.segmentsHistory = (action.payload as any).segmentsHistory as any;
                }
                if (action.payload.footerButtonsHistory) {
                    state.footerButtonsHistory = action.payload.footerButtonsHistory as any;
                }
                if ((action.payload as any).footerSurveyHistory) {
                    state.footerSurveyHistory = (action.payload as any).footerSurveyHistory as any;
                }
                // 会话初始化同步isOpen
                if ('is_open' in action.payload) {
                    state.isOpen = !!action.payload.is_open;
                } else {
                    state.isOpen = true;
                }
                // 初始化时默认关闭问卷可填写，等待触发survey或关闭后开启
                state.isSurveyEnabled = false;
            })
            .addCase(initializeSession.rejected, (state, action) => {
                state.isSessionLoading = false;
                state.error = action.error.message || '初始化会话失败';
            })
            // 创建新会话
            .addCase(createNewSession.pending, (state) => {
                state.isSessionLoading = true;
                state.error = null;
            })
            .addCase(createNewSession.fulfilled, (state, action) => {
                state.isSessionLoading = false;
                state.currentSessionId = action.payload.sessionId;
                // 清除旧消息，设置新消息（欢迎消息）
                state.messages = action.payload.messages;
                state.streamingAssistantMessage = '';
                state.streamingBubbleLoading = false;
                state.status = 'idle';
                // 清除工具活动和历史
                state.toolActivities = { byId: {}, order: [] };
                state.toolActivitiesHistory = {};
                state.streamingSegments = [];
                state.segmentsHistory = {};
                state.streamError = null;
                state.error = null;
                state.isOpen = true;
            })
            .addCase(createNewSession.rejected, (state, action) => {
                state.isSessionLoading = false;
                state.error = action.error.message || '创建新会话失败';
            })
            // 发送消息
            .addCase(sendMessage.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(sendMessage.fulfilled, (state) => {
                state.isLoading = false;
                // 不在这里重复添加用户消息，因为在sendMessage函数中已经添加了
            })
            .addCase(sendMessage.rejected, (state, action) => {
                state.isLoading = false;
                state.status = 'error';
                state.error = action.error.message || 'Failed to send message. Please try again.';
            });
    }
});

export const {
    clearMessages,
    setError,
    setStreamError,
    setCurrentSessionId,
    setCurrentAgent,
    clearSession,
    setStreamingStatus,
    updateStreamingAssistantMessage,
    setStreamingAssistantMessage,
    setCurrentMessageType,
    resetStreamingAssistantMessage,
    setStreamingBubbleLoading,
    setIsAiStreaming,
    addMessage,
    // 新增导出
    setSurveyEnabled,
    finalizeStreamingAssistantMessage,
    finalizeStreamingAssistantMessageWithId,
    resetSessionContext,
    finalizeAndSnapshotOnError,
    snapshotToolActivitiesToMessage,
    upsertToolCalls,
    appendToolResults,
    setToolActivitiesCompleted,
    resetToolActivities,
    appendToolSegment,
    appendContentSegment,
    snapshotSegmentsToMessage,
    resetStreamingSegments,
    setStreamingFooterButtons,
    snapshotFooterButtonsToMessage,
    setFooterButtonsHistoryEntry,
    clearAllFooterButtonsHistory,
    setIsOpen,
    setActiveForm,
    // 新增：中断流式任务
    abortStream
} = chatSlice.actions;

// thunk: 用于新建/重置对话，彻底中断流并重置状态
export const abortStreamAsync = () => (dispatch: any) => {
    abortCurrentStream();
    dispatch({ type: 'chat/abortStream' });
};

export default chatSlice.reducer;
